import React from 'react'
import { RestClient } from '@guillotinaweb/react-gmi'
import {
  getProductName,
  getShopCountryHeaders,
  getStoreFromCountry,
  getTotalStock,
} from '../utils'

export const ErpClientContext = React.createContext(null)

export class ErpClient {
  constructor(url, auth) {
    url = url || '/db/erp'
    this.url = url
    this.rest = new RestClient(url, auth)
  }

  async getImportedInvoice(template) {
    const res = await this.rest.post('/orders/@imported_invoice_data', {
      template,
    })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getImportedInvoiceV2(template) {
    const res = await this.rest.post('/orders/@imported_invoice_data_v2', {
      template,
    })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getAllPaymentCommissionCompanies() {
    const res = await this.rest.get(
      '/orders/@get_all_payment_commission_companies'
    )

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getImportedInvoiceEurotax() {
    const res = await this.rest.get('/orders/@get_all_invoices_eurotax')

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getImportedInvoiceWarehouseCost() {
    const res = await this.rest.get('/orders/@get-worker-cost')

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getProvidersMarketplace() {
    const res = await this.fetch('/@proveedores-marketplace')

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getProductsWithMatch() {
    const res = await this.fetch('/@get-products-with-match')

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async editMatchs(data) {
    const res = await this.rest.post('/@edit-product-with-match', {
      data,
    })
    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async deleteMatch(data) {
    const res = await this.rest.post('/@delete-product-match', {
      data,
    })
    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async getTotalOpinions() {
    const res = await this.fetch(`/opinions/@total`)

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async deletePriceComparator(data) {
    const res = await this.rest.delete('/@price-comparator', {
      data,
    })
    return await res.json()
  }

  async upsertPriceComparator(data, country) {
    const res = await this.rest.patch('/@price-comparator', {
      data,
      country,
    })
    if (res.status >= 300) {
      throw new Error(await res.text())
    }
    return await res.json()
  }

  async getPriceComparator(group, country = 'es', page = 0) {
    let params = {
      groups: [group],
      serializer: 'summary-all-opts',
      opciones: {
        estado: ['selling'],
      },
    }

    // Load top 25 ranking products (with pagination)
    if (!group) {
      const size = 25
      params = {
        size,
        cursor: page * size,
        serializer: 'summary-all-opts',
        sort_by: [
          {
            field: 'opciones.in_stock',
            order: 'asc',
          },
          {
            field: 'ranking',
            order: 'asc',
          },
        ],
        tipo: '/vi',
        opciones: {
          estado: ['selling'],
        },
      }
    }

    const { search } = await fetch(`/db/guillotina/@search-product`, {
      method: 'POST',
      headers: this.getHeaders({ country, shop: getStoreFromCountry(country) }),
      body: JSON.stringify(params),
    }).then((r) => r.json())

    const prods = await this.rest
      .post('/@price-comparator', { country, groups: search.map((p) => p.id) })
      .then((r) => r.json())

    const competence = prods.reduce((acc, c) => {
      if (!acc[c.product_group]) acc[c.product_group] = [c]
      else acc[c.product_group].push(c)
      return acc
    }, {})
    let result = []
    let withoutOption = []

    for (let prod of search) {
      const options = prod.opciones || []
      const comp = competence[prod.id] || [null]

      for (let prodCompetence of comp) {
        let [option] = options

        if (prodCompetence) {
          option = options.filter(
            (o) =>
              o.anyada === prodCompetence.anyada &&
              o.capacidad === prodCompetence.capacity
          )[0]
        }

        if (!option) {
          withoutOption.push(prodCompetence)
          continue
        }

        result.push({
          product_group: prod.id,
          delivery_country: country,
          ...(prodCompetence || {}),
          id_producto: option.referencia,
          our_price: option.precio_sin_impuestos,
          our_name: getProductName(
            prod.nombre,
            option.anyada,
            option.capacidad
          ),
          our_offer_price: option.oferta?.precio_oferta,
          our_taxes: option.impuestos,
          path: prod.path,
        })
      }
    }

    return [result, withoutOption]
  }

  async getOpinions(status = 'pend', page = 0, pageSize = 10) {
    const res = await this.fetch(
      `/opinions/@list?status=${status}&page=${page}&page_size=${pageSize}`
    )

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return res.json()
  }

  async editOpinion(id, opinion, titulo, nombre_usuario, nota, estado, shop) {
    const path = `/opinions/${shop[0].toUpperCase()}${id}/`
    const body = JSON.stringify({
      opinion,
      titulo,
      nombre_usuario,
      nota,
      estado,
    })
    const res = await this.fetch(path, { method: 'PATCH', body })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return true
  }

  async createOffers(offers) {
    const path = '/offers/'
    const body = JSON.stringify(offers)
    const res = await this.fetch(path, { method: 'POST', body })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return true
  }

  async getFreeShippingRates() {
    const path = '/promos_trg/@search'
    const method = 'POST'
    const response = await this.fetch(path, { method })

    return response
  }

  async createFreeShippingRate(freeShippingRate) {
    const path = '/promos_trg/'
    const method = 'POST'
    const body = JSON.stringify(freeShippingRate)
    const res = await this.fetch(path, { method, body })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return true
  }

  async deleteFreeShippingRate(id) {
    const path = `/promos_trg/${id}`
    const method = 'DELETE'
    const response = await this.fetch(path, { method })

    return response
  }

  async publishOpinion(id, shop) {
    const path = `/opinions/${shop[0].toUpperCase()}${id}/@publish`
    const res = await this.fetch(path, { method: 'POST' })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return true
  }

  async refuseOpinion(id, shop) {
    const path = `/opinions/${shop[0].toUpperCase()}${id}/@refuse`
    const res = await this.fetch(path, { method: 'POST' })

    if (res.status >= 300) {
      throw new Error(await res.text())
    }

    return true
  }

  async getAnalytics(since, to, interval = 'month') {
    const res = await this.rest.post('/analytics/@billing', {
      since,
      to,
      interval,
    })
    return await res.json()
  }

  async fetch(path, { method = 'GET', body } = {}) {
    let data = { method, body }
    data.headers = this.getHeaders() || {}
    return await fetch(`${this.url}${path}`, data)
  }

  async get(path, shop = 'vinissimus') {
    let data = {}
    data.headers = this.getHeaders() || {}
    data.headers['x-shop'] = shop
    return await fetch(`${this.url}${path}`, data)
  }

  getHeaders(info) {
    const token = localStorage.getItem('token')
    if (!token) {
      return {}
    }
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
      ...getShopCountryHeaders(info),
    }
  }

  async request(path, method, body, headers) {
    let data = {}
    body = body || {}
    data.method = method
    data.body = JSON.stringify(body)
    data.headers = headers || this.getHeaders()
    return await fetch(`${this.url}${path}`, data)
  }

  async fetchOrder(id) {
    const token = localStorage.getItem('token')
    if (!token) return {}

    const res = await this.fetch(`/orders/${id}/@full`, {
      headers: this.getHeaders(),
    })

    if (!res.ok) return null

    const order = await res.json()
    const info = { shop: order.shop, country: order.foto_direccion.id_pais }

    // Add new unitary prices to each line
    const prods = await fetch('/db/guillotina/@prod-by-referencia', {
      method: 'POST',
      headers: this.getHeaders(info),
      body: JSON.stringify({
        referencias: order.lines.map((line) => line.id_producto),
        opciones: {
          estado: ['selling', 'notavailable'],
        },
      }),
    }).then((res) => res.json())

    order.lines = order.lines.map((l) => {
      const option = prods[l.id_producto]?.opciones?.[0] || {}
      const stock = getTotalStock(option)

      return {
        ...l,
        new_taxes: option.impuestos ?? l.tipo_iva,
        new_price: option.precio_sin_impuestos ?? l.base_imponible,
        old_price: l.base_imponible,
        old_taxes: l.tipo_iva,
        stock: Math.max(stock, l.cantidad),
        option,
      }
    })

    return order
  }

  async getProductsOutCatalog() {
    const response = await this.fetch('/@ids-products-out-catalog', {
      method: 'GET',
      headers: this.getHeaders(),
    })

    return response.json()
  }

  async checkProductsElasticsearch(data) {
    await this.fetch('/@check-products-no-patch', {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        ids_products: data.ids_products,
      }),
    })
  }

  getAnalytics() {
    return this.fetch('/analytics/@product-traffic-and-performance', {
      headers: this.getHeaders(),
    }).then((r) => r.json())
  }

  /**
   * Update Order Products
   *
   * @param {object} order
   * @param {object} data
   * @example data = {
   * "product_lines": [
   *         {"id_producto": "p1", "cantidad": 333},
   *         {"id_producto": "p3", "cantidad": 3},
   *     ],
   *     "check_last_bottles": true,
   *     "change_price": false,
   *     "forced": true,
   * }
   * @returns Promise
   */
  async updateOrderProducts(order, data) {
    const shop = order.shop
    const id = `${shop[0].toUpperCase()}${order.id_pedido}`
    return this.fetch(`/orders/${id}/@update-products`, {
      method: 'POST',
      headers: this.getHeaders({
        shop,
        country: order.foto_direccion.id_pais,
      }),
      body: JSON.stringify(data),
    })
  }
}
